import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Collapse,
  Box,
  useTheme,
  useMediaQuery,
  Divider,
  alpha,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  ShoppingCart as VentasIcon,
  Store as ComprasIcon,
  Inventory as InventarioIcon,
  AccountBalance as TesoreriaIcon,
  ExpandMore,
  Category as CategoryIcon,
  SwapHoriz as SwapHorizIcon,
  ListAlt as ListAltIcon,
  QuestionAnswer as ConsultasIcon,
  Assessment as AssessmentIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Receipt as ReceiptIcon,
  AccountBalanceWallet as DisponibilidadesIcon
} from '@mui/icons-material';

const drawerWidth = 280;
const drawerWidthCollapsed = 72;

interface MenuItemProps {
  text: string;
  icon: React.ReactNode;
  path: string;
  subItems?: MenuItemProps[];
}

interface SidebarProps {
  mobileOpen?: boolean;
  onMobileClose?: () => void;
  desktopOpen?: boolean;
  onDesktopToggle?: () => void;
}

// Función para obtener el color del icono según el tipo de item
const getIconColor = (itemText: string, isActive: boolean): string => {
  return '#00548f'; // Azul #00548f para todos los iconos
};

// Función para obtener el color del icono de subitem
const getSubIconColor = (parentText: string, isActive: boolean): string => {
  return '#00548f'; // Azul #00548f para todos los iconos de subitems
};

const menuItems: MenuItemProps[] = [
  { text: 'Indicadores', icon: <DashboardIcon />, path: '/dashboard' },
  {
    text: 'Ventas',
    icon: <VentasIcon />, 
    path: '/ventas',
    subItems: [
      { text: 'Cotizaciones', icon: <ListAltIcon />, path: '/ventas/cotizaciones' },
      { text: 'Pedidos', icon: <ListAltIcon />, path: '/ventas/pedidos' },
      { text: 'Facturas', icon: <ListAltIcon />, path: '/ventas/facturas' },
      { text: 'Notas de Crédito', icon: <ListAltIcon />, path: '/ventas/notas-credito' },
      { text: 'Informes', icon: <AssessmentIcon />, path: '/ventas/informes' },
      { text: 'Clientes', icon: <CategoryIcon />, path: '/ventas/clientes' }
    ]
  },
  {
    text: 'Compras',
    icon: <ComprasIcon />,
    path: '/compras',
    subItems: [
      { text: 'Informes', icon: <AssessmentIcon />, path: '/compras/informes' },
      { text: 'Proveedores', icon: <CategoryIcon />, path: '/compras/proveedores' }
    ]
  },
  { text: 'Consultas', icon: <ConsultasIcon />, path: '/consultas' },
  { 
    text: 'Inventario', 
    icon: <InventarioIcon />, 
    path: '/inventario',
    subItems: [
      { text: 'Ajustes y Transf.', icon: <SwapHorizIcon />, path: '/inventario/transferencias' },
      { text: 'Informes', icon: <AssessmentIcon />, path: '/inventario/informes' },
      { text: 'Productos', icon: <CategoryIcon />, path: '/inventario/productos' },
      { text: 'Ramos', icon: <CategoryIcon />, path: '/inventario/ramos' },
      { text: 'Líneas', icon: <CategoryIcon />, path: '/inventario/lineas' },
      { text: 'Clases', icon: <CategoryIcon />, path: '/inventario/clases' }
    ]
  },
  { 
    text: 'Tesorería',
    icon: <TesoreriaIcon />, 
    path: '/tesoreria', 
    subItems: [
      { text: 'Planilla de Caja', icon: <ReceiptIcon />, path: '/tesoreria/movimientos-caja' },
      { text: 'Disponibilidades', icon: <DisponibilidadesIcon />, path: '/tesoreria/disponibilidades' },
      { text: 'Transferencias', icon: <SwapHorizIcon />, path: '/tesoreria/transferencias' },
      { text: 'Recibos', icon: <ListAltIcon />, path: '/tesoreria/recibos' },
      { text: 'Orden de Pago', icon: <ListAltIcon />, path: '/tesoreria/orden-pago' },
      { text: 'Movimientos Varios', icon: <ListAltIcon />, path: '/tesoreria/movimientos-varios' },
      { text: 'Movimientos Bancarios', icon: <ListAltIcon />, path: '/tesoreria/movimientos-bancarios' },
      { text: 'Informes', icon: <AssessmentIcon />, path: '/tesoreria/informes' },
      { text: 'Bancos', icon: <CategoryIcon />, path: '/tesoreria/bancos' },
      { text: 'Carteras', icon: <CategoryIcon />, path: '/tesoreria/carteras' },
      { text: 'Grupos de Caja', icon: <CategoryIcon />, path: '/tesoreria/grupos-mcp' },
      { text: 'Tarjetas', icon: <CategoryIcon />, path: '/tesoreria/tarjetas' }
    ]
  },
  // { text: 'Contabilidad', icon: <ContabilidadIcon />, path: '/contabilidad' },
];

const Sidebar: React.FC<SidebarProps> = ({ mobileOpen, onMobileClose, desktopOpen = true, onDesktopToggle }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Estado para expandir/cerrar submenús
  const [inventarioOpen, setInventarioOpen] = useState(() => location.pathname.startsWith('/inventario'));
  const [ventasOpen, setVentasOpen] = useState(() => location.pathname.startsWith('/ventas'));
  const [tesoreriaOpen, setTesoreriaOpen] = useState(() => location.pathname.startsWith('/tesoreria'));
  const [comprasOpen, setComprasOpen] = useState(() => location.pathname.startsWith('/compras'));

  const handleItemClick = (item: MenuItemProps) => {
    // Si el sidebar está colapsado, expandirlo primero
    if (!isMobile && !desktopOpen && onDesktopToggle) {
      onDesktopToggle();
      // Esperar a que se expanda antes de ejecutar la acción
      setTimeout(() => {
        if (item.subItems) {
          // Si tiene subitems, expandir el submenu correspondiente
          if (item.text === 'Inventario') {
            setInventarioOpen(true);
          } else if (item.text === 'Compras') {
            setComprasOpen(true);
          } else if (item.text === 'Ventas') {
            setVentasOpen(true);
          } else if (item.text === 'Tesorería') {
            setTesoreriaOpen(true);
          }
        } else {
          // Si no tiene subitems, navegar directamente
          navigate(item.path);
        }
      }, 350); // Esperar un poco más que la animación (300ms)
      return;
    }

    // Comportamiento normal cuando está expandido
    if (item.text === 'Inventario' && item.subItems) {
      setInventarioOpen(!inventarioOpen);
    } else if (item.text === 'Compras' && item.subItems) {
      setComprasOpen(!comprasOpen);
    } else if (item.text === 'Ventas' && item.subItems) {
      setVentasOpen(!ventasOpen);
    } else if (item.text === 'Tesorería' && item.subItems) {
      setTesoreriaOpen(!tesoreriaOpen);
    } else {
      navigate(item.path);
      if (isMobile && onMobileClose) {
        onMobileClose();
      }
    }
  };

  const handleSubItemClick = (path: string) => {
    navigate(path);
    if (isMobile && onMobileClose) {
      onMobileClose();
    }
  };

  const isItemActive = (item: MenuItemProps) => {
    if (item.path === location.pathname) return true;
    if (item.subItems) {
      return item.subItems.some(subItem => subItem.path === location.pathname);
    }
    return false;
  };

  const isSubItemActive = (path: string) => {
    return location.pathname === path;
  };

  const getOpenState = (itemText: string) => {
    switch (itemText) {
      case 'Inventario': return inventarioOpen;
      case 'Ventas': return ventasOpen;
      case 'Tesorería': return tesoreriaOpen;
      case 'Compras': return comprasOpen;
      default: return false;
    }
  };

  const drawer = (
    <Box 
      sx={{ 
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRight: '1px solid rgba(0, 0, 0, 0.08)',
        background: 'linear-gradient(180deg, #ffffff 0%, #f8f9fa 100%)',
        position: 'relative'
      }}
    >
      {/* Botón para colapsar/expandir sidebar en desktop */}
      {!isMobile && (
        <Tooltip title={desktopOpen ? "Colapsar menú" : "Expandir menú"} placement="right">
          <IconButton
            onClick={onDesktopToggle}
            sx={{
              position: 'absolute',
              top: 8,
              right: desktopOpen ? 8 : '50%',
              transform: desktopOpen ? 'none' : 'translateX(50%)',
              zIndex: 10,
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              width: desktopOpen ? 'auto' : 40,
              height: 40,
              color: '#00548f',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 1)',
                boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
              },
              transition: 'all 0.3s ease-in-out'
            }}
          >
            {desktopOpen ? <ChevronLeftIcon /> : <ChevronRightIcon />}
          </IconButton>
        </Tooltip>
      )}

      {/* Lista de items */}
      <Box sx={{ flexGrow: 1, overflow: 'auto', py: 1, pt: desktopOpen ? 1 : 9, pb: 7 }}>
        <List disablePadding>
          {menuItems.map((item, index) => {
            const isActive = isItemActive(item);
            const isOpen = getOpenState(item.text);
            const hasSubItems = !!item.subItems;

            return (
              <React.Fragment key={item.text}>
                <ListItem disablePadding sx={{ px: desktopOpen ? 1.5 : 0.5, mb: 0.5 }}>
                  <Tooltip 
                    title={!desktopOpen ? item.text : ''} 
                    placement="right"
                    arrow
                  >
                    <ListItemButton
                      onClick={() => handleItemClick(item)}
                      sx={{
                        borderRadius: 2,
                        py: 1.25,
                        px: desktopOpen ? 2 : 1,
                        minHeight: 48,
                        justifyContent: desktopOpen ? 'flex-start' : 'center',
                        transition: 'all 0.2s ease-in-out',
                        backgroundColor: isActive && !hasSubItems 
                          ? alpha(theme.palette.primary.main, 0.15) 
                          : 'transparent',
                        borderLeft: isActive && !hasSubItems
                          ? `4px solid ${theme.palette.primary.main}`
                          : '4px solid transparent',
                        '&:hover': {
                          backgroundColor: isActive && !hasSubItems
                            ? alpha(theme.palette.primary.main, 0.2)
                            : alpha(theme.palette.primary.main, 0.08),
                          borderLeftColor: isActive && !hasSubItems
                            ? theme.palette.primary.main
                            : alpha(theme.palette.primary.main, 0.5),
                          transform: desktopOpen ? 'translateX(4px)' : 'scale(1.1)',
                        },
                        '&:active': {
                          transform: desktopOpen ? 'translateX(2px)' : 'scale(1.05)',
                        },
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: desktopOpen ? 40 : 'auto',
                          justifyContent: 'center',
                          color: getIconColor(item.text, isActive && !hasSubItems),
                          transition: 'color 0.2s ease-in-out',
                        }}
                      >
                        {item.icon}
                      </ListItemIcon>
                      {desktopOpen && (
                        <>
                          <ListItemText
                            primary={item.text}
                            primaryTypographyProps={{
                              fontSize: '0.9375rem',
                              fontWeight: isActive && !hasSubItems ? 600 : 500,
                              color: isActive && !hasSubItems
                                ? theme.palette.primary.main
                                : 'text.primary',
                            }}
                          />
                          {hasSubItems && (
                            <Box
                              sx={{
                                color: isActive
                                  ? theme.palette.primary.main
                                  : 'text.secondary',
                                transition: 'transform 0.2s ease-in-out, color 0.2s ease-in-out',
                                transform: isOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
                              }}
                            >
                              <ExpandMore />
                            </Box>
                          )}
                        </>
                      )}
                    </ListItemButton>
                  </Tooltip>
                </ListItem>

                {/* Submenús - Solo mostrar si está expandido */}
                {hasSubItems && desktopOpen && (
                  <Collapse 
                    in={isOpen} 
                    timeout="auto" 
                    unmountOnExit
                    sx={{ pl: 2 }}
                  >
                    <List disablePadding>
                      {item.subItems!.map((subItem) => {
                        const isSubActive = isSubItemActive(subItem.path);
                        return (
                          <ListItem key={subItem.path} disablePadding sx={{ mb: 0.25 }}>
                            <Tooltip 
                              title={subItem.text} 
                              placement="right"
                              arrow
                            >
                              <ListItemButton
                                onClick={() => handleSubItemClick(subItem.path)}
                                sx={{
                                  borderRadius: 1.5,
                                  py: 0.875,
                                  px: 2,
                                  ml: 1,
                                  minHeight: 40,
                                  transition: 'all 0.2s ease-in-out',
                                  backgroundColor: isSubActive
                                    ? alpha(theme.palette.primary.main, 0.18)
                                    : 'transparent',
                                  borderLeft: isSubActive
                                    ? `4px solid ${theme.palette.primary.main}`
                                    : '4px solid transparent',
                                  '&:hover': {
                                    backgroundColor: isSubActive
                                      ? alpha(theme.palette.primary.main, 0.25)
                                      : alpha(theme.palette.primary.main, 0.1),
                                    borderLeftColor: isSubActive
                                      ? theme.palette.primary.main
                                      : alpha(theme.palette.primary.main, 0.5),
                                    transform: 'translateX(2px)',
                                  },
                                }}
                              >
                                <ListItemIcon
                                  sx={{
                                    minWidth: 36,
                                    color: getSubIconColor(item.text, isSubActive),
                                    fontSize: '1.1rem',
                                  }}
                                >
                                  {subItem.icon}
                                </ListItemIcon>
                                <ListItemText
                                  primary={subItem.text}
                                  primaryTypographyProps={{
                                    fontSize: '0.875rem',
                                    fontWeight: isSubActive ? 600 : 400,
                                    color: isSubActive
                                      ? theme.palette.primary.main
                                      : 'text.primary',
                                  }}
                                />
                              </ListItemButton>
                            </Tooltip>
                          </ListItem>
                        );
                      })}
                    </List>
                  </Collapse>
                )}

                {/* Separador después de ciertos items - Solo mostrar si está expandido */}
                {desktopOpen && (index === 0 || item.text === 'Consultas') && (
                  <Box sx={{ py: 0.5 }}>
                    <Divider sx={{ borderColor: alpha(theme.palette.primary.main, 0.1) }} />
                  </Box>
                )}
              </React.Fragment>
            );
          })}
        </List>
      </Box>
    </Box>
  );

  return (
    <Box
      component="nav"
      sx={{ 
        width: { md: desktopOpen ? drawerWidth : drawerWidthCollapsed }, 
        flexShrink: { md: 0 },
        transition: 'width 0.3s ease-in-out'
      }}
    >
      <Drawer
        variant={isMobile ? "temporary" : "permanent"}
        open={isMobile ? mobileOpen : desktopOpen}
        onClose={onMobileClose}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', md: 'block' },
          '& .MuiDrawer-paper': {
            width: !isMobile 
              ? (desktopOpen ? drawerWidth : drawerWidthCollapsed)
              : drawerWidth,
            boxSizing: 'border-box',
            mt: isMobile ? 8 : 8,
            background: 'linear-gradient(180deg, #ffffff 0%, #f8f9fa 100%)',
            borderRight: '1px solid rgba(0, 0, 0, 0.08)',
            boxShadow: 'none',
            zIndex: (theme) => theme.zIndex.drawer,
            transition: 'width 0.3s ease-in-out',
            overflowX: 'hidden',
          },
        }}
      >
        {drawer}
      </Drawer>
    </Box>
  );
};

export default Sidebar;
